// src/data/soRules.ts
// ------------------
// Repository for `so_rules` (db-69) — the ordered Sales Order routing/grouping
// rules the SO engine reads at Confirm & Send (docs/so-rules-engine.md). Backs the
// Manage → Unleashed → SO Rules admin screen. Typed via getTypedClient().

import type { Database } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";

export type SoRuleRow = Database["public"]["Tables"]["so_rules"]["Row"];
type SoRuleInsert = Database["public"]["Tables"]["so_rules"]["Insert"];
type SoRulePatch = Database["public"]["Tables"]["so_rules"]["Update"];
type WriteResult = { error: { message: string } | null };

// All rules in priority order (the engine and the admin screen both want this
// order). Includes inactive rows — the admin console shows them; the engine
// filters to active. null on error / no client.
//
// The secondary `.order("id")` is the tiebreak. Nothing stops two rules sharing a
// priority, and Postgres gives NO guaranteed order for tied rows — so without this
// the engine's stable sort preserved an arbitrary arrival order and the same
// request could route differently between runs. Ordering by id makes the outcome
// deterministic; soRulesEngine applies the identical tiebreak so the two agree.
// It does NOT resolve the ambiguity — findPriorityConflicts (soRuleConflicts.ts)
// surfaces that on the admin screen so it can be fixed in the data.
export async function listSoRules(): Promise<SoRuleRow[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("so_rules")
    .select("*")
    .order("priority", { ascending: true })
    .order("id", { ascending: true })
    .limit(500);
  if (error) {
    logger.warn("listSoRules failed:", error.message);
    return null;
  }
  return (data as unknown as SoRuleRow[]) ?? [];
}

// Create a rule. Loose object cast to the generated Insert shape (repo write
// convention). Logs on error and returns { error }.
export async function createSoRule(row: object): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c.from("so_rules").insert(row as SoRuleInsert);
  if (error) logger.warn("createSoRule failed:", error.message);
  return { error };
}

// Update a rule by id (edit / reorder / activate-deactivate). Stamps updated_at.
export async function updateSoRuleById(
  id: number | undefined,
  patch: object
): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const body = { ...(patch as SoRulePatch), updated_at: new Date().toISOString() };
  const { error } = await c
    .from("so_rules")
    .update(body)
    .eq("id", id ?? 0);
  if (error) logger.warn("updateSoRuleById failed:", error.message);
  return { error };
}
